import { useState } from "react";
import {
  ArrowLeft,
  Activity,
  Heart,
  Flame,
  Moon,
  Footprints,
  TrendingUp,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

const ActivityTrends = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<"week" | "month" | "year">("week");

  const weeklyData = [
    { day: "Mon", steps: 8200, calories: 420, heartRate: 68, sleep: 7.5 },
    { day: "Tue", steps: 9100, calories: 480, heartRate: 72, sleep: 8.2 },
    { day: "Wed", steps: 7500, calories: 380, heartRate: 65, sleep: 6.8 },
    { day: "Thu", steps: 10200, calories: 520, heartRate: 75, sleep: 8.5 },
    { day: "Fri", steps: 8800, calories: 450, heartRate: 70, sleep: 7.2 },
    { day: "Sat", steps: 12500, calories: 620, heartRate: 78, sleep: 9.0 },
    { day: "Sun", steps: 6800, calories: 340, heartRate: 62, sleep: 8.5 },
  ];

  const monthlyData = [
    { week: "Week 1", steps: 58000, calories: 2900, heartRate: 70, sleep: 52 },
    { week: "Week 2", steps: 62000, calories: 3100, heartRate: 72, sleep: 54 },
    { week: "Week 3", steps: 55000, calories: 2750, heartRate: 68, sleep: 50 },
    { week: "Week 4", steps: 60000, calories: 3000, heartRate: 71, sleep: 53 },
  ];

  const yearlyData = [
    { month: "Jan", steps: 240000, calories: 12000, heartRate: 70, sleep: 210 },
    { month: "Feb", steps: 250000, calories: 12500, heartRate: 69, sleep: 215 },
    { month: "Mar", steps: 260000, calories: 13000, heartRate: 71, sleep: 220 },
  ];

  const getData = () => {
    switch (timeframe) {
      case "week":
        return weeklyData;
      case "month":
        return monthlyData;
      case "year":
        return yearlyData;
      default:
        return weeklyData;
    }
  };

  const getXDataKey = () => {
    switch (timeframe) {
      case "week":
        return "day";
      case "month":
        return "week";
      case "year":
        return "month";
      default:
        return "day";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Activity Trends
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Timeframe Selector */}
        <div className="flex gap-2">
          {["week", "month", "year"].map(t => (
            <Button
              key={t}
              variant={timeframe === t ? "default" : "outline"}
              size="sm"
              onClick={() => setTimeframe(t as typeof timeframe)}
              className="h-9 flex-1 text-xs capitalize"
            >
              {t}
            </Button>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="rounded-xl border border-border/50 bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Footprints className="h-4 w-4 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground">Total Steps</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {timeframe === "week"
                ? "63,100"
                : timeframe === "month"
                  ? "235K"
                  : "750K"}
            </div>
            <p className="text-chart-2 mt-1 flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3" />
              +12% from last {timeframe}
            </p>
          </Card>

          <Card className="rounded-xl border border-border/50 bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                <Flame className="h-4 w-4 text-accent" />
              </div>
              <span className="text-xs text-muted-foreground">Calories</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {timeframe === "week"
                ? "3,210"
                : timeframe === "month"
                  ? "11.7K"
                  : "37.5K"}
            </div>
            <p className="text-chart-2 mt-1 flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3" />
              +8% from last {timeframe}
            </p>
          </Card>

          <Card className="rounded-xl border border-border/50 bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/10">
                <Heart className="h-4 w-4 text-secondary" />
              </div>
              <span className="text-xs text-muted-foreground">
                Avg Heart Rate
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground">70 BPM</div>
            <p className="mt-1 text-xs text-muted-foreground">Normal range</p>
          </Card>

          <Card className="rounded-xl border border-border/50 bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-info/10 flex h-8 w-8 items-center justify-center rounded-lg">
                <Moon className="text-info h-4 w-4" />
              </div>
              <span className="text-xs text-muted-foreground">Avg Sleep</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {timeframe === "week"
                ? "7.9h"
                : timeframe === "month"
                  ? "7.8h"
                  : "7.7h"}
            </div>
            <p className="text-chart-2 mt-1 text-xs">Excellent</p>
          </Card>
        </div>

        {/* Steps Trend */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">
            Steps Trend
          </h2>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getData()}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey={getXDataKey()}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Bar
                  dataKey="steps"
                  fill="hsl(var(--primary))"
                  radius={[8, 8, 0, 0]}
                  name="Steps"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Calories & Heart Rate Trend */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">
            Calories & Heart Rate
          </h2>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={getData()}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey={getXDataKey()}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="calories"
                  stroke="hsl(var(--accent))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--accent))", r: 4 }}
                  name="Calories Burned"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="heartRate"
                  stroke="hsl(var(--secondary))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--secondary))", r: 4 }}
                  name="Heart Rate (BPM)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Sleep Trend */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">
            Sleep Trend
          </h2>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getData()}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey={getXDataKey()}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Bar
                  dataKey="sleep"
                  fill="hsl(var(--info))"
                  radius={[8, 8, 0, 0]}
                  name="Sleep Hours"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ActivityTrends;
